import { clsx } from 'clsx';
import {
  forwardRef,
  type ElementType,
  type ReactNode,
  type ComponentPropsWithRef,
} from 'react';

/* ------------------------------------------------------------------ */
/*  Variant + size token maps                                         */
/* ------------------------------------------------------------------ */

const VARIANT = {
  primary: clsx(
    'bg-primary text-white border-primary',
    'hover:bg-primary-dim hover:border-primary-dim',
    'active:bg-primary-dim active:brightness-90',
  ),
  secondary: clsx(
    'bg-surface-1 text-ink-muted border-border',
    'hover:bg-surface-2 hover:border-border-strong hover:text-ink',
    'active:bg-surface-3',
  ),
  ghost: clsx(
    'bg-transparent text-ink-muted border-transparent',
    'hover:bg-surface-2 hover:text-ink',
    'active:bg-surface-3',
  ),
  danger: clsx(
    'bg-danger-soft text-danger border-danger/20',
    'hover:bg-danger hover:text-on-fill hover:border-danger',
    'active:brightness-90',
  ),
  success: clsx(
    'bg-success-soft text-success border-success/20',
    'hover:bg-success hover:text-on-fill hover:border-success',
    'active:brightness-90',
  ),
} as const;

const SIZE = {
  xs: 'h-7 px-2 text-2xs gap-1 rounded-sm',
  sm: 'h-8 px-2.5 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-[13px] gap-1.5 rounded-md',
  lg: 'h-10 px-5 text-sm gap-2 rounded-lg',
} as const;

const ICON_SIZE = { xs: 'h-3.5 w-3.5', sm: 'h-4 w-4', md: 'h-4 w-4', lg: 'h-5 w-5' } as const;

/* ------------------------------------------------------------------ */
/*  Spinner                                                           */
/* ------------------------------------------------------------------ */

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={clsx('animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export type ButtonVariant = keyof typeof VARIANT;
export type ButtonSize = keyof typeof SIZE;

/** Props owned by <Button> — everything else passes through to the root element. */
type OwnProps<C extends ElementType = 'button'> = {
  /** Visual style. @default "secondary" */
  variant?: ButtonVariant;
  /** Size preset. @default "md" */
  size?: ButtonSize;
  /** Show a spinner and disable interaction. */
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: ReactNode;
  /** Icon rendered after the label. */
  iconRight?: ReactNode;
  /** Render the root as a different element (e.g. `"a"` or a router `Link`). */
  as?: C;
  /** Extra class names merged onto the root element. */
  className?: string;
  children?: ReactNode;
};

export type ButtonProps<C extends ElementType = 'button'> = OwnProps<C> &
  Omit<ComponentPropsWithRef<C>, keyof OwnProps<C>>;

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

/**
 * Shared `<Button>` — production-grade, polymorphic, token-driven.
 *
 * ```tsx
 * <Button variant="primary" size="md" icon={<PlusIcon />} loading={isSubmitting}>
 *   Create deal
 * </Button>
 *
 * <Button as="a" href="/docs" variant="ghost" size="sm">
 *   View docs
 * </Button>
 * ```
 */
export const Button = forwardRef(function Button<C extends ElementType = 'button'>(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconRight,
    as,
    className,
    disabled,
    children,
    ...rest
  }: ButtonProps<C>,
  ref: React.Ref<Element>,
) {
  const Tag: ElementType = as ?? 'button';
  const isDisabled = disabled || loading;

  return (
    <Tag
      ref={ref}
      disabled={Tag === 'button' ? isDisabled : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      className={clsx(
        // Layout
        'inline-flex items-center justify-center font-medium whitespace-nowrap select-none',
        // Transitions
        'transition-all duration-150 ease-out',
        // Focus ring — consistent across variants
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        // Border baseline (variants override colors)
        'border',
        // Active press effect
        'active:scale-[0.98]',
        // Size
        SIZE[size],
        // Variant
        VARIANT[variant],
        // Disabled / loading
        isDisabled && 'pointer-events-none opacity-50 cursor-not-allowed',
        className,
      )}
      {...rest}
    >
      {/* Leading icon or spinner */}
      {loading ? (
        <Spinner className={ICON_SIZE[size]} />
      ) : icon ? (
        <span className={clsx('shrink-0', ICON_SIZE[size])}>{icon}</span>
      ) : null}

      {/* Label */}
      {children != null && <span className="truncate">{children}</span>}

      {/* Trailing icon */}
      {iconRight && !loading && (
        <span className={clsx('shrink-0', ICON_SIZE[size])}>{iconRight}</span>
      )}
    </Tag>
  );
}) as <C extends ElementType = 'button'>(props: ButtonProps<C>) => JSX.Element;
